import { StrictMode, Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider } from './context/AuthContext.jsx'
import { ConsentProvider } from './context/ConsentContext.jsx'
import { AdvertisingProvider } from './context/AdvertisingContext.jsx'
import AutoAdsBoundary from './components/ads/AutoAdsBoundary.jsx'
import AdvertisingDiagnostics from './components/ads/AdvertisingDiagnostics.jsx'
import { retireAdvertisingWorker } from './lib/retireAdvertisingWorker.js'
import PageResources from './components/consent/PageResources.jsx'
import { IS_DEBUG_CHAT_ENABLED } from './config/env.js'
import { EDITOR_APP_PATHS } from './lib/appRoutes.js'
import 'react-phone-number-input/style.css'
import './index.css'
import './App.css'

const App = lazy(() => import('./App.jsx'))
const NotFoundPage = lazy(() => import('./components/NotFoundPage.jsx'))
const SeoReferencePage = lazy(() => import('./components/seo/SeoReferencePage.jsx'))
const PasswordResetPage = lazy(() => import('./components/auth/PasswordResetPage.jsx'))
const LearningPage = lazy(() => import('./components/LearningPage.jsx'))
const BackofficePage = lazy(() => import('./components/backoffice/BackofficePage.jsx'))
const DebugChatPage = lazy(() => import('./components/debug/DebugChatPage.jsx'))
const DebugOffersPage = lazy(() => import('./components/debug/DebugOffersPage.jsx'))
const SeoLandingPage = lazy(() => import('./components/seo/SeoLandingPage.jsx'))
const SeoExercisesPage = lazy(() => import('./components/seo/SeoExercisesPage.jsx'))
const SeoDownloadPage = lazy(() => import('./components/seo/SeoDownloadPage.jsx'))
const SeoDownloadRedirect = lazy(() => import('./components/seo/SeoDownloadPage.jsx').then(module => ({ default: module.SeoDownloadRedirect })))
const NexoPage = lazy(() => import('./components/nexo/NexoPage.jsx'))

void retireAdvertisingWorker()
createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <ConsentProvider>
          <AdvertisingProvider>
          <AdvertisingDiagnostics />
          <AutoAdsBoundary>
          <Suspense fallback={<div className="min-h-screen bg-background text-foreground" />}>
            <Routes>
            {['/sobre/', '/visual-gusto/', '/professores/'].map(route => <Route key={route} path={route} element={<SeoReferencePage route={route} />} />)}
            <Route path="/recuperar-senha" element={<PasswordResetPage requestOnly />} />
            <Route path="/redefinir-senha" element={<PasswordResetPage />} />
            <Route path="/jogos" element={<Navigate to="/jogos/nexo/" replace />} />
            <Route path="/jogos/nexo" element={<NexoPage />} />
            <Route path="/aprender/exercicios-portugol-resolvidos-visualg" element={<SeoExercisesPage />} />
            <Route path="/baixar-visualg" element={<SeoDownloadPage />} />
            <Route path="/baixar-visualg/download" element={<SeoDownloadRedirect />} />
            <Route path="/aprender/*" element={<LearningPage />} />
            <Route path="/o-que-e-portugol" element={<SeoLandingPage slug="o-que-e-portugol" />} />
            <Route path="/pseudocodigo" element={<SeoLandingPage slug="pseudocodigo" />} />
            <Route path="/o-que-e-visualg" element={<SeoLandingPage slug="o-que-e-visualg" />} />
            <Route path="/o-que-e-programacao" element={<SeoLandingPage slug="o-que-e-programacao" />} />
            <Route path="/como-aprender-python" element={<SeoLandingPage slug="como-aprender-python" />} />
            <Route path="/o-que-e-linguagem-de-programacao" element={<SeoLandingPage slug="o-que-e-linguagem-de-programacao" />} />
            <Route path="/backoffice/*" element={<BackofficePage />} />
            {IS_DEBUG_CHAT_ENABLED && (
              <Route path="/debug-chat" element={<DebugChatPage />} />
            )}
            {IS_DEBUG_CHAT_ENABLED && (
              <Route path="/debug-offers" element={<DebugOffersPage />} />
            )}
            {EDITOR_APP_PATHS.map(path => <Route key={path} path={path} element={<App />} />)}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          <PageResources />
          </AutoAdsBoundary>
          <Toaster richColors position="bottom-right" />
          </AdvertisingProvider>
        </ConsentProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
)
